// RISK MODEL (RiskAdapter).
// Two clearly separated concepts:
//   1) marketTypeRisk  — a TRANSPARENT, rule-based classification of the BET TYPE
//      (e.g. Over 3.5 is inherently higher-variance than Over 1.5). This is a
//      property of the market itself, not a per-match prediction, so it is honest
//      to display when clearly labelled as "market type".
//   2) matchRisk       — a per-match risk metric. The SDIP backend does not currently
//      expose a transparent per-match risk value, so this is UNAVAILABLE until a
//      legitimate source/calculation is connected.

import { resolveMarket, MarketRisk } from "@/presentation/marketRegistry";

export type RiskLevel = "LOW" | "MEDIUM" | "HIGH";

export interface RiskAssessment {
  available: boolean;
  level: RiskLevel | null;
  basis: "market_type" | "backend" | null; // where the level came from
  label: string;                            // localized display label
}

const LEVEL_LABEL: Record<RiskLevel, { tr: string; en: string }> = {
  LOW: { tr: "Düşük Risk", en: "Low Risk" },
  MEDIUM: { tr: "Orta Risk", en: "Medium Risk" },
  HIGH: { tr: "Yüksek Risk", en: "High Risk" },
};

function normalize(r: MarketRisk): RiskLevel {
  if (r === "LOW") return "LOW";
  if (r === "HIGH") return "HIGH";
  return "MEDIUM"; // LIVE / MEDIUM collapse to MEDIUM band for filtering
}

// Transparent market-type risk band. Always available (it classifies the market, not the match).
export function marketTypeRisk(code: string | null | undefined, lang: "tr" | "en" = "tr"): RiskAssessment {
  const m = resolveMarket(code, lang);
  const level = normalize(m.risk);
  return { available: true, level, basis: "market_type", label: LEVEL_LABEL[level][lang] };
}

// Per-match risk. No transparent backend source yet -> unavailable.
// When the backend later supplies a real risk value/score, wire it here.
export function matchRisk(_backendRisk?: unknown): RiskAssessment {
  return { available: false, level: null, basis: null, label: "" };
}

export function riskLevelLabel(level: RiskLevel, lang: "tr" | "en" = "tr"): string {
  return LEVEL_LABEL[level][lang];
}

export const RISK_LEVELS: RiskLevel[] = ["LOW", "MEDIUM", "HIGH"];
